// Insurance — tenant-facing credit-insurance layer. Only mounted when the tenant's plan/features
// entitlement includes `insurance` (the nav item in Dashboard.js gates on it; the backend
// enforces it again on create). It reads the tenant's live AR portfolio, surfaces the uninsured
// risk, generates AI-style coverage recommendations, and lets the client raise cover requests to
// their distributor/carrier (Allianz Trade, Coface, Atradius).
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box, Typography, Button, Chip, CircularProgress, Alert, Tooltip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { toast } from "react-toastify";
import { getReportingPortfolio } from "./api/underwritingApi";
import { getInsuranceRequests, createInsuranceRequest } from "./api/insuranceApi";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import { C, bandColor, bandLabel, toNum, fmtUSD, fmtFull } from "./reporting/reportingUtils";
import InsuranceOnboarding from "./components/InsuranceOnboarding";
import BillInsuranceCoverageDashboard, { BILL_TENANT_ID } from "./components/BillInsuranceCoverageDashboard";

// ── Coverage products & carriers ──────────────────────────────────────────────
const PRODUCTS = [
  { key: "whole_turnover", label: "Whole-Turnover", blurb: "Cover your entire receivables book against buyer default and insolvency.", accent: C.blue },
  { key: "single_risk", label: "Single-Risk", blurb: "Named cover on one high-value or higher-risk buyer.", accent: C.violet },
  { key: "top_up", label: "Top-Up", blurb: "Extra limit on a buyer beyond your existing carrier's approved line.", accent: C.amber },
  { key: "surety", label: "Surety / Bonds", blurb: "Performance, advance-payment and contract bonds for your obligations.", accent: C.green },
];
const PRODUCT_LABEL = Object.fromEntries(PRODUCTS.map((p) => [p.key, p.label]));

const CARRIERS = [
  { key: "allianz_trade", label: "Allianz Trade" },
  { key: "coface", label: "Coface" },
  { key: "atradius", label: "Atradius" },
];
const CARRIER_LABEL = Object.fromEntries(CARRIERS.map((c) => [c.key, c.label]));

const STATUS_STYLE = {
  submitted: { label: "Submitted", bg: "#EAF2FE", fg: C.blue },
  under_review: { label: "Under review", bg: "#FFF3DE", fg: "#B4791E" },
  corrections_requested: { label: "Corrections requested", bg: "#FDECEC", fg: C.red },
  resubmitted: { label: "Resubmitted", bg: "#EAF2FE", fg: C.blue },
  approved: { label: "Approved", bg: "#E7F4E7", fg: C.green },
  quoted: { label: "Quoted", bg: "#FFF3DE", fg: "#B4791E" },
  bound: { label: "Bound", bg: "#E7F4E7", fg: C.green },
  declined: { label: "Declined", bg: "#FDECEC", fg: C.red },
  withdrawn: { label: "Withdrawn", bg: "#EEF1F7", fg: C.muted },
};

const Card = ({ children, sx }) => (
  <Box sx={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: "12px", p: "18px 20px", ...sx }}>
    {children}
  </Box>
);

const Kpi = ({ label, value, sub, accent }) => (
  <Card sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
    <Typography sx={{ fontSize: 11.5, letterSpacing: ".06em", textTransform: "uppercase", color: C.muted, fontWeight: 700 }}>{label}</Typography>
    <Typography sx={{ fontSize: 26, fontWeight: 700, color: accent || C.ink, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
    {sub && <Typography sx={{ fontSize: 12.5, color: C.muted }}>{sub}</Typography>}
  </Card>
);

// ── Recommendation engine ─────────────────────────────────────────────────────
// Turns the live portfolio + the buyers already covered into a prioritised list of coverage
// actions. Deterministic and explainable (no server round-trip) so the tenant sees *why*.
function buildRecommendations(rows, coveredCompanyIds, totals) {
  const recs = [];
  const uninsured = rows.filter((r) => r.companyId && !coveredCompanyIds.has(r.companyId));

  // 1) High-risk uninsured buyers with real exposure → single-risk cover, most exposed first.
  const highRisk = uninsured
    .map((r) => ({ ...r, _score: toNum(r.score), _exp: toNum(r.exposure) }))
    .filter((r) => r._score != null && r._score >= 6.6 && r._exp > 0)
    .sort((a, b) => b._exp * b._score - a._exp * a._score)
    .slice(0, 6);
  for (const r of highRisk) {
    const limit = Math.min(r._exp, toNum(r.recLimit) || r._exp);
    recs.push({
      id: `single_${r.companyId}`,
      priority: 1,
      product: "single_risk",
      companyId: r.companyId,
      companyName: r.name,
      exposure: r._exp,
      arcScore: r._score,
      sector: r.sector || null,
      country: r.region || null,
      requestedLimit: Math.round(limit),
      title: `Insure ${r.name}`,
      reason: `High risk (ARC ${r._score.toFixed(1)}) with ${fmtUSD(r._exp)} live exposure and no cover in place.`,
    });
  }

  // 2) Concentration: a single uninsured buyer > 15% of the whole book → single-risk / top-up.
  const concentrated = uninsured
    .map((r) => ({ ...r, _exp: toNum(r.exposure) }))
    .filter((r) => r._exp > 0 && totals.exposure > 0 && r._exp / totals.exposure >= 0.15)
    .sort((a, b) => b._exp - a._exp)
    .slice(0, 3);
  for (const r of concentrated) {
    if (recs.some((x) => x.companyId === r.companyId)) continue; // already flagged as high-risk
    const pct = Math.round((r._exp / totals.exposure) * 100);
    recs.push({
      id: `conc_${r.companyId}`,
      priority: 2,
      product: "single_risk",
      companyId: r.companyId,
      companyName: r.name,
      exposure: r._exp,
      arcScore: toNum(r.score),
      sector: r.sector || null,
      country: r.region || null,
      requestedLimit: Math.round(r._exp),
      title: `Reduce concentration on ${r.name}`,
      reason: `${pct}% of your total exposure sits on this one uninsured buyer.`,
    });
  }

  // 3) Book-wide: material uninsured exposure and no whole-turnover request → whole-turnover.
  if (!coveredCompanyIds.has("__WHOLE_TURNOVER__") && totals.uninsuredExposure > 0 && rows.length >= 5) {
    recs.push({
      id: "whole_turnover",
      priority: 3,
      product: "whole_turnover",
      companyId: null,
      companyName: null,
      exposure: totals.uninsuredExposure,
      requestedLimit: null,
      title: "Cover the whole book",
      reason: `${fmtUSD(totals.uninsuredExposure)} of receivables across ${totals.uninsuredCount} buyers is currently uninsured.`,
    });
  }

  return recs.sort((a, b) => a.priority - b.priority);
}

export default function Insurance() {
  const isAdmin = useSelector((s) => s.userInfo.isAdmin);
  const currentTenant = useSelector((s) => s.userInfo.tenantId);
  const URLTenant = getTenantIdFromURL(); // set on /tenant-dashboard/:id/insurance (admin view)
  const tenantId = URLTenant || currentTenant;
  const isBillTenant = tenantId === BILL_TENANT_ID;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [dialog, setDialog] = useState(null); // { product, companyId, companyName, requestedLimit, ... }

  const load = async () => {
    if (!tenantId) { setError("No tenant selected"); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const [portfolio, reqRes] = await Promise.all([
        getReportingPortfolio(tenantId),
        getInsuranceRequests(tenantId).catch(() => ({ data: [] })),
      ]);
      setRows(portfolio?.rows || []);
      setRequests(reqRes?.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to load insurance data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  // Buyers already covered (any non-withdrawn/declined request), plus a sentinel for book-wide cover.
  const coveredCompanyIds = useMemo(() => {
    const set = new Set();
    for (const r of requests) {
      if (["withdrawn", "declined"].includes(r.status)) continue;
      if (r.product === "whole_turnover") set.add("__WHOLE_TURNOVER__");
      if (r.companyId) set.add(r.companyId);
    }
    return set;
  }, [requests]);

  const totals = useMemo(() => {
    let exposure = 0, insured = 0, uninsuredExposure = 0, uninsuredCount = 0, highRiskCount = 0;
    for (const r of rows) {
      const exp = toNum(r.exposure) || 0;
      exposure += exp;
      const covered = r.companyId && coveredCompanyIds.has(r.companyId);
      const bookCovered = coveredCompanyIds.has("__WHOLE_TURNOVER__");
      if (covered || bookCovered) insured += exp;
      else { uninsuredExposure += exp; if (exp > 0) uninsuredCount += 1; }
      const s = toNum(r.score);
      if (s != null && s >= 6.6) highRiskCount += 1;
    }
    return { exposure, insured, uninsuredExposure, uninsuredCount, highRiskCount };
  }, [rows, coveredCompanyIds]);

  const recommendations = useMemo(
    () => buildRecommendations(rows, coveredCompanyIds, totals),
    [rows, coveredCompanyIds, totals]
  );

  const coveragePct = totals.exposure > 0 ? Math.round((totals.insured / totals.exposure) * 100) : 0;

  const openRequest = (seed) => setDialog({
    product: "single_risk", carrier: "", companyId: null, companyName: null,
    requestedLimit: "", notes: "", source: "manual", recommendationId: null, ...seed,
  });

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, background: C.ground, minHeight: "100%" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <ShieldOutlinedIcon sx={{ color: C.blue }} />
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.ink, lineHeight: 1.1 }}>Insurance</Typography>
            <Typography sx={{ fontSize: 13, color: C.muted }}>{isBillTenant ? "Bill credit-card coverage — requested limits, supported exposure and paths to close each gap." : "Protect your receivables — recommendations from your live portfolio."}</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<ShieldOutlinedIcon />} onClick={() => openRequest({})}
          sx={{ textTransform: "none", background: C.blue, fontWeight: 700 }}>
          Start onboarding
        </Button>
      </Box>

      {loading && <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>}
      {!loading && error && (
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>
      )}

      {!loading && !error && (
        <>
          {isBillTenant && (
            <BillInsuranceCoverageDashboard
              tenantId={tenantId}
              rows={rows}
              isAdmin={isAdmin}
              onRequestCover={openRequest}
            />
          )}

          {/* KPIs */}
          {!isBillTenant && <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 2 }}>
            <Kpi label="Total exposure" value={fmtUSD(totals.exposure)} sub={`${rows.length} buyers`} />
            <Kpi label="Insured" value={fmtUSD(totals.insured)} sub={`${coveragePct}% of book`} accent={C.green} />
            <Kpi label="Uninsured" value={fmtUSD(totals.uninsuredExposure)} sub={`${totals.uninsuredCount} buyers`} accent={C.amber} />
            <Kpi label="High-risk buyers" value={String(totals.highRiskCount)} sub="ARC ≥ 6.6" accent={totals.highRiskCount ? C.red : C.ink} />
          </Box>}

          {/* Recommendations */}
          <Card sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", background: "linear-gradient(135deg, #F6F8FF 0%, #FFFFFF 70%)" }}>
            <Box sx={{ width: 42, height: 42, borderRadius: "50%", display: "grid", placeItems: "center", background: "#EEE9FF", color: C.violet }}>
              <AutoAwesomeIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontWeight: 800, color: C.ink, fontSize: 15 }}>Guided insurance onboarding</Typography>
              <Typography sx={{ color: C.muted, fontSize: 12.75 }}>
                Reuse your live AR data, collect product-specific documents, auto-fill your partner’s application and get help with every missing item.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<ShieldOutlinedIcon />} onClick={() => openRequest({ product: "whole_turnover" })}
              sx={{ textTransform: "none", background: C.blue, fontWeight: 700 }}>
              Start or continue
            </Button>
          </Card>

          {/* Recommendations */}
          {!isBillTenant && <Card sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <AutoAwesomeIcon sx={{ color: C.violet, fontSize: 20 }} />
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.ink }}>Recommended coverage</Typography>
              <Chip size="small" label={`${recommendations.length}`} sx={{ height: 20, fontWeight: 700, background: "#F0ECFE", color: C.violet }} />
            </Box>
            {recommendations.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: C.muted }}>
                No open coverage gaps detected — your material and higher-risk exposures are already requested or covered.
              </Typography>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.25 }}>
                {recommendations.map((rec) => (
                  <Box key={rec.id} sx={{ border: `1px solid ${C.line}`, borderRadius: "10px", p: "14px 16px", display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                      <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: C.ink }}>{rec.title}</Typography>
                      <Chip size="small" label={PRODUCT_LABEL[rec.product]} sx={{ height: 20, fontWeight: 700, background: "#EEF3FB", color: C.blue }} />
                    </Box>
                    <Typography sx={{ fontSize: 12.75, color: C.slate, lineHeight: 1.4 }}>{rec.reason}</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                      {rec.arcScore != null && (
                        <Chip size="small" label={`ARC ${Number(rec.arcScore).toFixed(1)}`}
                          sx={{ height: 20, fontWeight: 700, color: "#fff", background: bandColor(rec.arcScore) }} />
                      )}
                      {rec.requestedLimit != null && (
                        <Typography sx={{ fontSize: 12.5, color: C.muted, fontVariantNumeric: "tabular-nums" }}>
                          Suggested limit {fmtFull(rec.requestedLimit)}
                        </Typography>
                      )}
                      <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                        onClick={() => openRequest({
                          product: rec.product, companyId: rec.companyId, companyName: rec.companyName,
                          requestedLimit: rec.requestedLimit ?? "", exposure: rec.exposure,
                          arcScore: rec.arcScore, sector: rec.sector, country: rec.country,
                          source: "recommendation", recommendationId: rec.id,
                        })}
                        sx={{ ml: "auto", textTransform: "none", fontWeight: 700, color: C.blue }}>
                        Request cover
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Card>}

          {/* Products */}
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em", mb: 1 }}>
            Coverage products
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 1.5, mb: 2 }}>
            {PRODUCTS.map((p) => (
              <Card key={p.key} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ width: 34, height: 34, borderRadius: "9px", display: "grid", placeItems: "center", background: `${p.accent}15` }}>
                  <ShieldOutlinedIcon sx={{ color: p.accent, fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.ink }}>{p.label}</Typography>
                <Typography sx={{ fontSize: 12.75, color: C.slate, lineHeight: 1.45, flex: 1 }}>{p.blurb}</Typography>
                <Button size="small" variant="outlined" onClick={() => openRequest({ product: p.key })}
                  sx={{ textTransform: "none", alignSelf: "flex-start", borderColor: C.line, color: C.ink, fontWeight: 700 }}>
                  Start application
                </Button>
              </Card>
            ))}
          </Box>

          {/* Requests */}
          <Card>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.ink, mb: 1.25 }}>Your requests</Typography>
            {requests.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: C.muted }}>No insurance requests yet. Raise one from a recommendation or a product above.</Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th, & td": { textAlign: "left", py: 1, px: 1, borderBottom: `1px solid ${C.line}`, fontSize: 13 } }}>
                  <Box component="thead">
                    <Box component="tr" sx={{ "& th": { color: C.muted, fontWeight: 700, fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".05em" } }}>
                      <th>Buyer</th><th>Product</th><th>Carrier</th><th>Limit</th><th>Status</th><th>Requested</th>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {requests.map((r) => {
                      const st = STATUS_STYLE[r.status] || STATUS_STYLE.submitted;
                      return (
                        <Box component="tr" key={r.id}>
                          <td style={{ fontWeight: 700, color: C.ink }}>{r.companyName || "Whole book"}</td>
                          <td>{PRODUCT_LABEL[r.product] || r.product}</td>
                          <td>{CARRIER_LABEL[r.carrier] || "—"}</td>
                          <td style={{ fontVariantNumeric: "tabular-nums" }}>{r.requestedLimit != null ? fmtFull(r.requestedLimit) : "—"}</td>
                          <td><Chip size="small" label={st.label} sx={{ height: 20, fontWeight: 700, background: st.bg, color: st.fg }} /></td>
                          <td style={{ color: C.muted }}>{r.createdAt ? String(r.createdAt).slice(0, 10) : "—"}</td>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </Card>
        </>
      )}

      <InsuranceOnboarding
        open={!!dialog}
        seed={dialog}
        tenantId={tenantId}
        rows={rows}
        onClose={() => setDialog(null)}
        onSubmitted={(created) => { setRequests((prev) => [created, ...prev.filter((item) => item.id !== created?.id)]); setDialog(null); }}
      />
    </Box>
  );
}

// ── Request dialog ────────────────────────────────────────────────────────────
function RequestDialog({ open, seed, tenantId, rows, onClose, onSubmitted }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && seed) setForm({ ...seed }); }, [open, seed]);

  const needsBuyer = form.product === "single_risk" || form.product === "top_up";
  const buyerOptions = useMemo(
    () => rows.filter((r) => r.companyId).map((r) => ({ id: r.companyId, name: r.name, score: r.score, exposure: r.exposure, sector: r.sector, region: r.region, recLimit: r.recLimit })),
    [rows]
  );

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const pickBuyer = (companyId) => {
    const b = buyerOptions.find((x) => x.id === companyId);
    if (!b) { set({ companyId, companyName: null }); return; }
    set({
      companyId: b.id, companyName: b.name, arcScore: toNum(b.score), exposure: toNum(b.exposure),
      sector: b.sector || null, country: b.region || null,
      requestedLimit: form.requestedLimit || Math.round(Math.min(toNum(b.exposure) || 0, toNum(b.recLimit) || toNum(b.exposure) || 0)) || "",
    });
  };

  const submit = async () => {
    if (!form.carrier) { toast.error("Choose a carrier"); return; }
    if (needsBuyer && !form.companyId) { toast.error("Choose a buyer for this cover"); return; }
    setBusy(true);
    try {
      const res = await createInsuranceRequest({
        tenantId,
        product: form.product,
        carrier: form.carrier,
        companyId: needsBuyer ? form.companyId : null,
        companyName: needsBuyer ? form.companyName : null,
        requestedLimit: form.requestedLimit === "" ? null : Number(form.requestedLimit),
        exposure: form.exposure ?? null,
        arcScore: form.arcScore ?? null,
        sector: form.sector ?? null,
        country: form.country ?? null,
        notes: form.notes || null,
        source: form.source || "manual",
        recommendationId: form.recommendationId || null,
      });
      toast.success("Request sent to your insurance partner");
      onSubmitted(res?.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to submit request");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
        Request cover
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <TextField label="Product" select size="small" InputLabelProps={{ shrink: true }} value={form.product || "single_risk"} onChange={(e) => set({ product: e.target.value })} sx={{ mt: 0.75 }}>
          {PRODUCTS.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
        </TextField>

        {needsBuyer && (
          <TextField label="Buyer" select size="small" value={form.companyId || ""} onChange={(e) => pickBuyer(e.target.value)}>
            <MenuItem value=""><em>Select a buyer…</em></MenuItem>
            {buyerOptions.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}{b.score != null ? ` · ARC ${Number(b.score).toFixed(1)}` : ""}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField label="Carrier" select size="small" value={form.carrier || ""} onChange={(e) => set({ carrier: e.target.value })}>
          <MenuItem value=""><em>Select a carrier…</em></MenuItem>
          {CARRIERS.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
        </TextField>

        <TextField label="Requested limit (USD)" size="small" type="number" value={form.requestedLimit ?? ""}
          onChange={(e) => set({ requestedLimit: e.target.value })}
          helperText={form.product === "whole_turnover" ? "Optional for whole-turnover cover" : " "} />

        {/* Auto-filled context — shown read-only so the client sees what the carrier receives. */}
        {(form.exposure != null || form.arcScore != null) && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: -0.5 }}>
            {form.exposure != null && <Chip size="small" label={`Exposure ${fmtUSD(form.exposure)}`} sx={{ height: 22, background: "#EEF3FB", color: C.blue, fontWeight: 700 }} />}
            {form.arcScore != null && <Chip size="small" label={`ARC ${Number(form.arcScore).toFixed(1)} · ${bandLabel(form.arcScore)}`} sx={{ height: 22, color: "#fff", background: bandColor(form.arcScore), fontWeight: 700 }} />}
          </Box>
        )}

        <TextField label="Notes to your insurance partner" size="small" multiline minRows={2}
          value={form.notes || ""} onChange={(e) => set({ notes: e.target.value })} />

        <Divider />
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", color: C.muted }}>
          <WarningAmberIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: 12 }}>
            Submitting shares this buyer's risk profile with your distributor and the selected carrier for quoting.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={busy} sx={{ background: C.blue, textTransform: "none", fontWeight: 700 }}>
          {busy ? "Submitting…" : "Submit request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
